import { CollectionPage } from 'selectors/consumerPageSelectors';
import { ExperienceTags } from 'components/www/availability-search/filters/SearchFilterExperienceTags';
import { FilterType, OfferingSearchTypeOrBusinessType } from 'utils/searchBar';
import { LocalDate } from 'js-joda';
import { QueryTypes } from 'routes/www/availability-search/buildQueryTypeForCity';
import { SearchParams } from 'actions/AvailabilityActions';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer, core } from '@tock/coretypes';
import { convertQueryStringToSearchParams } from 'components/search/convertQueryStringToSearchParams';
import { isQueryTimeValid, setDefaultSearchQueryParams } from 'utils/citySearchUtils';
import _ from 'lodash';

interface CollectionPageState {
  stateType: 'collectionPage';
  metroArea: core.MetroArea;
  queryType: QueryTypes;
}

interface MetroAreaState {
  stateType: 'city';
  metroArea: core.MetroArea;
  queryType: QueryTypes;
}

interface BusinessGroupState {
  stateType: 'group';
  businessGroup: BusinessGroup;
}

interface TogoState {
  stateType: 'togo';
}

/**
 * Search mode has various information that is useful depending on what
 * type of search scenario is involved
 */
export type SearchMode = MetroAreaState | BusinessGroupState | TogoState | CollectionPageState;

export interface SearchFilterParams extends ExperienceTags {
  /**
   * the type that the search will be filtered on, aka TOGO, RESERVATIONS, etc.
   */
  type?: consumer.OfferingSearchType;
  cuisines?: string[];
  priceRange?: string;
}

export interface SearchQueryStringParams extends Partial<SearchFilterParams> {
  city?: string;
  date?: string;
  dateEnd?: string;
  time?: string;
  size?: string;
  /**
   * used in the initial booking flow on search and
   * when showing a menu
   */
  experienceId?: string;
  businessId?: string;
  businessDomain?: string;
  showModal?: 'menu' | 'experience';
  /**
   * can be a string or an array of strings for multi-term search
   */
  q?: string | string[];
  /**
   * if this is present, will be used in the header to be the display,
   * along with a different header format, and usually there will be multiple
   * q params being used alongside this
   */
  displayTerm?: string;
  businessGroupId?: string;
  latlng?: string;
  businessType?: string;
  tock_medium?: string;
  tock_source?: string;
  topLeftLat?: string;
  topLeftLon?: string;
  bottomRightLat?: string;
  bottomRightLon?: string;
}

export interface BusinessGroup {
  id: number;
  name: string;
  slug: string;
}

export type SearchType = 'city' | 'group' | 'togo' | 'collectionPage';

export function getHasSearchQueryParams(
  searchType: SearchType,
  query: SearchQueryStringParams
): boolean {
  if (!query || (query && _.isEmpty(query))) {
    return false;
  }
  switch (searchType) {
    case 'group':
      return !_.isNil(query.date) || !_.isNil(query.time) || !_.isNil(query.size);
    case 'togo':
      return !_.isNil(query.city);
    case 'city':
    default:
      return (
        !_.isNil(query.date) || !_.isNil(query.time) || !_.isNil(query.size) || !_.isNil(query.q)
      );
  }
}

interface BuildQueryStringParamsOptions {
  queryStringParams: SearchQueryStringParams;
  mode: SearchMode;
  updatedSearchParams?: Partial<SearchParams>;
  patronLatLng?: { lat: number; lng: number };
}

/**
 * take the current query string, and add some params based on mode and existing values either
 * being present or not and based on search mode
 * @param options - object with:
 * @param options.queryStringParams - exsisting query string params in url
 * @param options.mode - mode to search on
 * @param options.updatedSearchParams - search params(not query string) to update - only used in old code, easier to merge the first parameter together before sending via object spread
 * @param options.patronLocation - current patron location to use for lat/lng
 */
export const buildQueryStringParams = ({
  queryStringParams,
  mode,
  updatedSearchParams,
  patronLatLng,
}: BuildQueryStringParamsOptions): SearchQueryStringParams => {
  const searchParams = convertQueryStringToSearchParams(queryStringParams);

  const combinedParams = {
    ...searchParams,
    ...updatedSearchParams,
  };

  const query: SearchQueryStringParams = {
    ...combinedParams,
    date: combinedParams.date?.toString(),
    dateEnd: combinedParams.dateEnd?.toString(),
    size: combinedParams.size?.toString(),
    time: combinedParams.time?.toString(),
  };

  if (patronLatLng) {
    query.latlng = `${patronLatLng.lat},${patronLatLng.lng}`;
  } else if (searchParams.latlng) {
    query.latlng = searchParams.latlng;
  }

  // only city and group allow filter by type, to go is currenlty always the same type
  if (
    mode.stateType === 'city' ||
    mode.stateType === 'group' ||
    mode.stateType === 'collectionPage'
  ) {
    query.type = combinedParams.type;
  }

  if (mode.stateType === 'city') {
    query.businessType = combinedParams.businessType;
  }

  if (
    (combinedParams.city && mode.stateType === 'togo') ||
    mode.stateType === 'city' ||
    mode.stateType === 'collectionPage'
  ) {
    // city in the second part of this scenario is now passed in the search results page, but not in the initial search.
    query.city =
      mode.stateType === 'togo'
        ? combinedParams.city
        : (combinedParams.city ?? mode.metroArea.name);
  }
  if (mode.stateType === 'group') {
    query.businessGroupId = `${mode.businessGroup.id}`;
  }

  if (queryStringParams.displayTerm) {
    query.displayTerm = queryStringParams.displayTerm;
  }

  if (
    queryStringParams.bottomRightLat &&
    queryStringParams.bottomRightLon &&
    queryStringParams.topLeftLat &&
    queryStringParams.topLeftLon
  ) {
    query.topLeftLon = queryStringParams.topLeftLon;
    query.topLeftLat = queryStringParams.topLeftLat;
    query.bottomRightLon = queryStringParams.bottomRightLon;
    query.bottomRightLat = queryStringParams.bottomRightLat;
  }

  return query;
};

export const getCollectionPageDatesFromConfig = (
  { dateStart, dateEnd = 1 }: Pick<CollectionPage, 'dateEnd' | 'dateStart'>,
  today: LocalDate
) => {
  // Computes an appropriate collectionDate, falling back to today if rawCollectionDate is undefined
  // or somehow before today.
  let collectionActiveStartDate;
  if (!dateStart || (collectionActiveStartDate = LocalDate.parse(dateStart)).isBefore(today)) {
    collectionActiveStartDate = today;
  }

  // If the active start date is different than the start date from the configuration, we want to
  // use the configuration start date as the basis for our search window
  const searchWindowStartDate = dateStart ? LocalDate.parse(dateStart) : today;

  const intendedDateEndFromStartDate = searchWindowStartDate.plusDays(dateEnd - 1);

  // Explaining the logic here - the default collection length in days (if collectionLengthInDays
  // is not specified) is 1 day. Hence the default-assignment in the destructuring above.
  // To have a 1-day collection, the start and end dates must be the same.
  // To have a 3-day collection, the end date must be 2 days after the start date. 3 days
  // afterward would be an off-by-one error.
  //
  // In case the user has some how specified a 0-length collection, a negative number, etc.,
  // we simply treat those values as equivalent to specifying "1".
  //
  // If the entire date range is prior to the start date, return end date of "undefined" to mimic
  // a standard "NOW" / "ASAP" search
  const collectionDateEnd =
    dateEnd <= 1 || intendedDateEndFromStartDate.isBefore(today)
      ? undefined
      : intendedDateEndFromStartDate;

  return { collectionDate: collectionActiveStartDate, collectionDateEnd };
};

export type SearchResultsOpenMode =
  | 'CLOSED'
  | 'SEARCH_OPEN'
  | 'TEXT_OPEN'
  | 'TYPE_OPEN'
  | 'CUISINES_OPEN'
  | 'SEARCH_FILTERS_OPEN';

/**
 * determines on the results page what can be filterd on
 * @param {SearchMode} mode
 */
export const useVisibleFilters = (mode?: SearchMode, type?: OfferingSearchTypeOrBusinessType) => {
  let visibleFilters: FilterType[] = [];
  if (!mode) {
    return visibleFilters;
  }
  switch (mode?.stateType) {
    case 'collectionPage': {
      // Temporary - dependent on how we choose to display Collection "Tag" filter
      visibleFilters = ['SIZE', 'DATE', 'TIME', 'TYPE', 'CITY', 'TAG'];
      break;
    }
    case 'city': {
      visibleFilters = mode.queryType.DISPLAYTERM
        ? ['SIZE', 'DATE', 'TIME', 'TYPE', 'CITY']
        : ['SIZE', 'DATE', 'TIME', 'TYPE', 'TEXT', 'CITY'];
      break;
    }
    case 'group':
      visibleFilters = ['SIZE', 'DATE', 'TIME', 'TYPE'];
      break;
    case 'togo':
      visibleFilters = ['DATE', 'CITY'];
      break;
    default:
      assertNever(mode);
  }

  if (type === 'EVENT') {
    visibleFilters = visibleFilters.filter(
      (visibleFilter) => visibleFilter !== 'SIZE' && visibleFilter !== 'TIME'
    );
  }

  return visibleFilters;
};

export const isValidQuery = (
  query: SearchQueryStringParams,
  mode: SearchMode,
  metroArea?: core.MetroArea
): boolean => {
  if (!metroArea) {
    return false;
  }
  const safeQuery = setDefaultSearchQueryParams(query, metroArea.timezone);
  if (mode.stateType === 'city' || mode.stateType === 'group') {
    if (!isQueryTimeValid(safeQuery)) {
      return false;
    }
  }
  return true;
};

export const useIsDateQuickFilterEnabled = (type?: OfferingSearchTypeOrBusinessType): boolean => {
  return type === 'EVENT';
};
